import React, { useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenuStateless from '@atlaskit/dropdown-menu';
import Icon from '@atlaskit/icon';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Section } from '@atlaskit/menu';
import { AutomationGlyph } from '@atlassian/jira-automation-menu/src/common/assets/automation';
import { Footer } from '@atlassian/jira-automation-platform';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { AutomationSubMenu } from './automation-submenu';
import messages from './messages';
import { TransitionsSubMenu } from './transitions-submenu';
import type { Props } from './types';

type ActionsMenuButtonProps = {
	isOpen: boolean;
	triggerRef: React.Ref<HTMLElement>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	bntProps: any;
};

export const useIsOpen = () => useState(false);

const ActionsMenuButton = ({ isOpen, triggerRef, bntProps }: ActionsMenuButtonProps) => {
	const { formatMessage } = useIntl();

	return (
		<Button
			{...bntProps}
			ref={triggerRef}
			appearance="subtle"
			isSelected={isOpen}
			iconAfter={<ChevronDownIcon label="" />}
			iconBefore={<Icon size="medium" glyph={AutomationGlyph} label="" secondaryColor="none" />}
			interactionName="auto-generated.jira-issue-field-status.ui.status-view.actions.actions-menu.button"
		>
			{formatMessage(messages.actions)}
		</Button>
	);
};

export const ActionsMenu = (props: Props) => {
	const {
		transitions,
		onSelect,
		rules,
		executeRule,
		executingRule,
		triggerManualRulesFetch,
		triggerTransitionsFetch,
		initialised,
		issueKey,
		issueId,
		baseAutomationUrl,
		canManageAutomations,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useIsOpen();
	const [hasFetched, setHasFetched] = useState(false);
	const castTransitions = useMemo(() => transitions || [], [transitions]);

	return (
		<DropdownMenuStateless
			trigger={({ triggerRef, ...bntProps }) => (
				<ActionsMenuButton isOpen={isOpen} triggerRef={triggerRef} bntProps={bntProps} />
			)}
			isOpen={isOpen}
			onOpenChange={() => {
				if (!isOpen) {
					const analyticsEvent = createAnalyticsEvent({
						actionSubject: 'menu',
						action: 'opened',
					});

					if (!hasFetched) {
						triggerManualRulesFetch();

						if (transitions === null) {
							triggerTransitionsFetch(analyticsEvent);
						}

						setHasFetched(true);
					}

					fireUIAnalytics(analyticsEvent, 'manualRulesActionsMenu', {
						userType: canManageAutomations ? 'admin' : 'endUser',
					});
				}

				setIsOpen(!isOpen);
			}}
		>
			<MenuItemContainer>
				<TransitionsSubMenu
					initialised={initialised}
					onSelect={onSelect}
					transitions={castTransitions}
					canManageAutomations={canManageAutomations}
				/>
				<AutomationSubMenu
					initialised={initialised}
					rules={rules}
					transitions={castTransitions}
					executeRule={executeRule}
					issueKey={issueKey}
					issueId={issueId}
					executingRule={executingRule}
					baseAutomationUrl={baseAutomationUrl}
					canManageAutomations={canManageAutomations}
				/>
			</MenuItemContainer>
			{canManageAutomations && <Footer baseAutomationUrl={baseAutomationUrl} />}
		</DropdownMenuStateless>
	);
};

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- To migrate as part of go/ui-styling-standard
export const MenuItemContainer = styled(Section)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 40}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${gridSize * 62}px`,
	overflow: 'auto',
});
